import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { mockContactSubmit, mockDownloadSample } from '../data/mockData';
import { Mail, User, MessageSquare, Send, Download } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadEmail, setDownloadEmail] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const result = await mockContactSubmit(formData);
      toast.success(result.message);
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Error al enviar el mensaje. Intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadSubmit = async (e) => {
    e.preventDefault();
    
    if (!downloadEmail) {
      toast.error('Por favor ingresa tu correo electrónico');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(downloadEmail)) {
      toast.error('Por favor ingresa un correo electrónico válido');
      return;
    }

    setIsDownloading(true);
    
    try {
      const result = await mockDownloadSample(downloadEmail);
      toast.success(result.message);
      setDownloadEmail('');
    } catch (error) {
      toast.error('Error al procesar la solicitud. Intenta nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-[#1a1c1b]">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="mb-6 inline-block">
              <span className="caption text-[#d9fb06] bg-[#3f4816] px-4 py-2 rounded-full">
                COMIENZA HOY
              </span>
            </div>
            
            <h2 className="heading-2 text-[#d9fb06] mb-6">
              Obtén una Muestra Gratuita
            </h2>
            
            <p className="body-medium text-[#888680] max-w-2xl mx-auto">
              Descarga los primeros capítulos y descubre cómo este libro puede 
              transformar tu carrera como entrenador de fútbol.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Download Form */}
            <div className="bg-[#302f2c] border-2 border-[#d9fb06] rounded-lg p-8 lg:p-10">
              <div className="mb-6">
                <h3 className="heading-5 text-[#d9fb06] mb-3 flex items-center gap-2">
                  <Download size={24} />
                  Descarga Muestra del Libro
                </h3>
                <p className="body-small text-[#888680]">
                  Ingresa tu correo y recibe los primeros capítulos directamente en tu bandeja.
                </p>
              </div>

              <form onSubmit={handleDownloadSubmit} className="space-y-4">
                <div>
                  <label htmlFor="download-email" className="caption text-[#d9fb06] mb-2 block">
                    CORREO ELECTRÓNICO
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#888680]" size={20} />
                    <Input
                      id="download-email"
                      type="email"
                      placeholder="tu@email.com"
                      value={downloadEmail}
                      onChange={(e) => setDownloadEmail(e.target.value)}
                      className="pl-12 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06]"
                      disabled={isDownloading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isDownloading}
                  className="btn-primary bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#d9fb06cc] w-full"
                >
                  {isDownloading ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Download className="mr-2" size={20} />
                      Descargar Muestra Gratis
                    </>
                  )}
                </Button>

                <p className="body-small text-[#888680] text-center">
                  No spam. Solo contenido de valor para entrenadores.
                </p>
              </form>
            </div>

            {/* Contact Form */}
            <div className="bg-[#302f2c] border border-[#3f4816] rounded-lg p-8 lg:p-10">
              <div className="mb-6">
                <h3 className="heading-5 text-[#d9fb06] mb-3 flex items-center gap-2">
                  <MessageSquare size={24} />
                  ¿Tienes Preguntas?
                </h3>
                <p className="body-small text-[#888680]">
                  Contáctanos y te responderemos a la brevedad.
                </p>
              </div>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="caption text-[#d9fb06] mb-2 block">
                    NOMBRE
                  </label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#888680]" size={20} />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="pl-12 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06]"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="caption text-[#d9fb06] mb-2 block">
                    CORREO ELECTRÓNICO
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#888680]" size={20} />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="tu@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="pl-12 bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06]"
                      disabled={isSubmitting}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="caption text-[#d9fb06] mb-2 block">
                    MENSAJE
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Escribe tu mensaje aquí..."
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={4}
                    className="bg-[#1a1c1b] border-[#3f4816] text-[#dfddd6] placeholder:text-[#888680] focus:border-[#d9fb06] resize-none"
                    disabled={isSubmitting}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-secondary border-[#d9fb06] text-[#d9fb06] hover:bg-[#d9fb06] hover:text-[#1a1c1b] w-full"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      <Send className="mr-2" size={20} />
                      Enviar Mensaje
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
